import {dataTestId} from '@hconnect/uikit'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import useDebounce from '@react-hook/debounce'
import React, {useEffect, useRef, useState} from 'react'
import {Tree, TreeApi} from 'react-arborist'

import {useUrlParam} from '../../../../../shared/hooks/useUrlParam'
import {useStoreUserValue} from '../../../../shared/hooks/userStorage/storeValue'
import {UserSignalGroup} from '../../../../shared/types/sensor.types'
import {
  usePlantMonitoring,
  usePlantMonitoringSelectors
} from '../../../context/PlantMonitoringContext'
import {useSignalTreeData} from '../../../hooks/useSignalTreeData'
import {Node, TreeNode} from '../SignalTreeNode'

export const AddGroupModal = ({
  isOpen,
  handleClose,
  userGroups
}: {
  isOpen: boolean
  handleClose: () => void
  userGroups: UserSignalGroup[]
}) => {
  const [selectedSignals, setSelectedSignals] = useState<string[]>([])
  const [search, setSearch] = useDebounce<string>('')
  const [groupName, setGroupName] = useState<string>()
  const [isError, setIsError] = useState<string>()
  const {mutateAsync: saveGroup, isPending} = useStoreUserValue()
  const {state} = usePlantMonitoring()
  const treeData = useSignalTreeData({equipmentOnly: false, providers: [], status: []})
  const treeRef = useRef<TreeApi<TreeNode>>()
  const plantId = useUrlParam('plantId')
  const {actions} = usePlantMonitoring()
  const {
    groupDataFromState: {columns, quickSelection, type}
  } = usePlantMonitoringSelectors()

  useEffect(() => {
    const group = userGroups.find((g) => g.name === state.activeGroup)
    const tree = treeRef.current
    if (group) {
      setGroupName(group.name)
      setSelectedSignals(group.signals)
      tree?.setSelection({
        ids: group.signals,
        anchor: null,
        mostRecent: null
      })
    }
    // the ref must be here, otherwise the selection will not work
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeGroup, userGroups, treeRef.current])

  const submitGroups = async () => {
    if (!groupName || groupName === '') {
      setIsError('Group name is required')
      return
    }

    const duplicate = userGroups.find((g) => g.name === groupName)
    if (duplicate && duplicate.name !== state.activeGroup) {
      setIsError('Group name is duplicate please select another name')
      return
    }

    // Edit existing group
    if (selectedSignals.length > 0 && state.activeGroup) {
      const updatedGroups = userGroups
        .filter((group) => group.type === 'user')
        .map((group) => {
          if (group.name === state.activeGroup) {
            return {
              ...group,
              name: groupName,
              signals: selectedSignals,
              chartsOrder: [
                ...group.chartsOrder.filter((chartId) => chartId.startsWith('combined')),
                ...selectedSignals
              ]
            }
          }
          return group
        })
      await saveGroup(
        {
          keyIdentifier: 'pm-user-groups',
          value: JSON.stringify(updatedGroups)
        },
        {
          onSuccess: () => {
            const updatedGroup = updatedGroups.find((g) => g.name === groupName)
            updatedGroup && actions.selectSignalGroup(updatedGroup)
          }
        }
      )
    }

    // Create new group
    if (selectedSignals.length > 0 && !state.activeGroup) {
      const newGroup: UserSignalGroup = {
        name: groupName,
        signals: selectedSignals,
        plantId: plantId,
        masterUpmId: null, // TODO: here we will potentially ad upm id of the Kiln etc. but currently new groups can be added only in plant monitoring,
        chartsOrder: selectedSignals,
        combined: [],
        columns,
        quickSelection,
        type
      }
      const newGroups = [...userGroups.filter((group) => group.type === 'user'), newGroup]

      await saveGroup(
        {
          keyIdentifier: 'pm-user-groups',
          value: JSON.stringify(newGroups)
        },
        {
          onSuccess: () => actions.selectSignalGroup(newGroup)
        }
      )
    }
    onClose()
  }

  const deleteGroup = async () => {
    const newGroups = userGroups
      .filter((group) => group.type === 'user')
      .filter((group) => group.name !== state.activeGroup)

    await saveGroup(
      {
        keyIdentifier: 'pm-user-groups',
        value: JSON.stringify(newGroups)
      },
      {onSuccess: () => actions.clearAllCharts()}
    )
    onClose()
  }

  const onClose = () => {
    setIsError(undefined)
    setGroupName(undefined)
    setSearch('')
    setSelectedSignals([])
    handleClose()
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose()} aria-labelledby="add-group-modal" fullWidth>
      <DialogTitle id="add-group-modal-title" variant="h4">
        New group
      </DialogTitle>
      <DialogContent sx={{overflowY: 'hidden'}}>
        <TextField
          label={'Group name'}
          fullWidth
          error={isError !== undefined}
          value={groupName ?? ''}
          helperText={isError}
          sx={{marginTop: 2}}
          onChange={(e) => {
            setGroupName(e.target.value)
            setIsError(undefined)
          }}
        />
        <Box mb={2} mt={2}>
          <Divider />
        </Box>
        <Box>
          <TextField
            label="Filter"
            fullWidth
            variant="filled"
            size={'small'}
            defaultValue={search}
            onChange={(e) => setSearch(e.target.value)}
            {...dataTestId('plant-structure-text-filter')}
            sx={{
              '& .MuiFilledInput-root': {
                backgroundColor: 'transparent',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: 'transparent'
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent'
                }
              },
              marginBottom: 3
            }}
          />
          <Tree
            ref={treeRef}
            data={treeData}
            openByDefault={true}
            rowHeight={60}
            width={550}
            indent={14}
            onSelect={(nodes) => {
              const s: string[] = nodes.map((n) => n.data.signalData?.localName ?? '')
              setSelectedSignals(s.filter((i) => i !== ''))
            }}
            searchTerm={search}
            searchMatch={(node) => node.data.label.toLowerCase().includes(search.toLowerCase())}
          >
            {(props) => Node({...props, displayCheckbox: true})}
          </Tree>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{backgroundColor: '#FFF'}}
          width={'100%'}
        >
          <Typography variant={'h3'}>{`${selectedSignals.length} sensors selected`}</Typography>
          <Stack gap={2} direction={'row'}>
            <Button onClick={onClose} variant={'outlined'} disabled={isPending}>
              Cancel
            </Button>
            {state.activeGroup && (
              <Button
                onClick={deleteGroup}
                color={'error'}
                variant={'contained'}
                disabled={isPending}
              >
                Delete
              </Button>
            )}
            <Button onClick={submitGroups} variant={'contained'} disabled={isPending}>
              Confirm
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
